import {
  DataTable,
  DatePicker,
  Field,
  FieldLabel,
  UserCombobox,
} from '@/components'
import { ABSENCE_TYPE_MANAGEMENT_QUERY } from '@/modules/timesheets/queries'
import { format, useFormWatch } from '@/utils'
import { useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf, VariablesOf } from 'gql.tada'
import { useCallback, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { Section } from '../common'
import { ManageAbsenceTypeDialog } from './manage-absence-type-dialog'

export type ManagementAbsenceType = ResultOf<
  typeof ABSENCE_TYPE_MANAGEMENT_QUERY
>['allAbsenceTypes'][number]

function useCreatePeriodReportText() {
  const translations = useTranslate({
    usedHours: [
      'settings.absence.manage-absences.used-hours',
      { count: <strong />, total: <strong /> },
    ],
    usedDays: [
      'settings.absence.manage-absences.used-days',
      { count: <strong />, total: <strong /> },
    ],
    usedAbsences: [
      'settings.absence.manage-absences.used-absences',
      { count: <strong />, total: <strong /> },
    ],
    remainingTimeOff: [
      'settings.absence.manage-absences.remaining-time-off',
      { count: <strong /> },
    ],
  })

  return useCallback(
    (type: ManagementAbsenceType): string => {
      const { preset, periodReport: report, durationMode } = type

      if (preset === 'FLEXTIME')
        return translations.remainingTimeOff({
          count: <strong>{report.hoursMax}</strong>,
        })

      if (report.absencesMax !== null)
        return translations.usedAbsences({
          count: <strong>{report.absencesTotal}</strong>,
          total: <strong>{report.absencesMax}</strong>,
        })

      if (durationMode === 'HOURLY')
        return translations.usedHours({
          count: <strong>{report.hoursUsed}</strong>,
          total: <strong>{report.hoursMax}</strong>,
        })

      if (durationMode === 'DAILY')
        return translations.usedDays({
          count: <strong>{Math.floor(report.hoursUsed / 24)}</strong>,
          total: <strong>{Math.floor(report.hoursMax / 24)}</strong>,
        })

      return '?'
    },
    [translations]
  )
}

const FilterFormSchema = z.object({
  user: z.string(),
  date: z.date(),
})

export function AbsenceManagement() {
  const translations = useTranslate({
    header: 'settings.absence.manage-absences',
    selectUser: 'settings.absence.manage-absences.select-user',

    user: 'settings.absence.manage-absences.user',
    select: 'common.select-ellipsis',
    date: 'settings.absence.manage-absences.date',

    absenceType: 'settings.absence.manage-absences.absence-type',
    intervalStart: 'settings.absence.manage-absences.interval-start',
    intervalEnd: 'settings.absence.manage-absences.interval-end',
    used: 'settings.absence.manage-absences.used',
  })

  const createPeriodReportText = useCreatePeriodReportText()

  const form = useForm({
    resolver: zodResolver(FilterFormSchema),
    defaultValues: {
      user: '',
      date: new Date(),
    },
  })

  const userId = useFormWatch(form, 'user')
  const date = useFormWatch(form, 'date')

  const queryVariables: VariablesOf<typeof ABSENCE_TYPE_MANAGEMENT_QUERY> = {
    userId,
    date: date.toISOString(),
  }

  const { data: managementData, loading: managementLoading } = useQuery(
    ABSENCE_TYPE_MANAGEMENT_QUERY,
    {
      skip: !userId,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      variables: queryVariables,
    }
  )

  const absenceTypes = useMemo(
    () => managementData?.allAbsenceTypes || [],
    [managementData]
  )

  const column = createColumnHelper<ManagementAbsenceType>()

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.absenceType,
        meta: {
          narrow: true,
        },
        cell(info) {
          const name = info.getValue()
          const color = info.row.original.color

          return (
            <div className="flex items-center gap-2">
              <div
                className="size-5 rounded-sm"
                style={{ backgroundColor: color }}
              />
              <span>{name}</span>
            </div>
          )
        },
      }),
      column.accessor('periodReport.periodStart', {
        header: translations.intervalStart,
        meta: {
          narrow: true,
        },
        cell(info) {
          const { preset } = info.row.original
          const periodStart = info.getValue()
          const start = format(new Date(periodStart), 'PP')

          return <span>{preset === 'FLEXTIME' ? '-' : start}</span>
        },
      }),
      column.accessor('periodReport.periodEnd', {
        header: translations.intervalEnd,
        meta: {
          narrow: true,
        },
        cell(info) {
          const { preset } = info.row.original
          const periodEnd = info.getValue()
          const end = format(new Date(periodEnd), 'PP')

          return <span>{preset === 'FLEXTIME' ? '-' : end}</span>
        },
      }),
      column.display({
        id: 'info',
        header: translations.used,
        cell(info) {
          const type = info.row.original
          return <span>{createPeriodReportText(type)}</span>
        },
      }),
      column.display({
        id: 'manage',
        meta: {
          narrow: true,
        },
        cell(info) {
          const type = info.row.original
          return <ManageAbsenceTypeDialog absenceType={type} date={date} />
        },
      }),
    ],
    [
      column,
      createPeriodReportText,
      date,
      translations.absenceType,
      translations.intervalEnd,
      translations.intervalStart,
      translations.used,
    ]
  )

  return (
    <Section header={translations.header}>
      <form className="flex gap-4">
        <Controller
          control={form.control}
          name="user"
          render={({ field }) => (
            <Field>
              <FieldLabel>{translations.user}</FieldLabel>

              <UserCombobox
                {...field}
                className="max-w-[350px]"
                mode="single"
                placeholder={translations.select}
              />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="date"
          render={({ field }) => (
            <Field>
              <FieldLabel>{translations.date}</FieldLabel>

              <DatePicker
                {...field}
                mode="single"
                onChange={value => {
                  if (!value) return
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
      </form>

      <DataTable
        columns={tableColumns}
        data={absenceTypes}
        loading={managementLoading}
        noDataText={
          !queryVariables.userId ? translations.selectUser : undefined
        }
      />
    </Section>
  )
}
